import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Mic, MicOff, Sparkles, Star, Trash2, Wand2, X } from 'lucide-react';
import {
  Alert, Button, Checkbox, Dialog, Input, Select, Textarea, cn,
} from '@dru/ui';
import {
  CONTEXT_PERMISSIONS, DURATION_UNITS, TEMPLATE_CONTEXT_LABEL, renderTemplate, resolveShortcut,
  type FieldValue, type ShortcutEntry, type TemplateContext, type TemplateField, type TemplateShape,
} from '@dru/clinical-math/smart-template';
import { api } from '../lib/api';
import { errorMessage, useAuth } from '../lib/auth';

/**
 * Clinical shortcuts + smart templates (Requirement 4): one composer for
 * every clinical free-text field.
 *
 * Two mechanisms, kept visibly apart:
 *
 *   · Templates and favourites write FIXED wording, by the same pure
 *     renderer the API runs. Nothing unselected or unfilled is written, and
 *     a blank finding is never written as "normal".
 *   · "Organise with AI" tidies what was typed or spoken into an English
 *     draft. It is marked as an AI draft with the model's name, the things
 *     it was unsure of, and any word it added that the notes did not say;
 *     the clinician has to tick that they checked it.
 *
 * Nothing reaches the field until the clinician presses "Add to note" or
 * "Replace note". The field's own save / sign flow is unchanged.
 */

interface Template extends TemplateShape {
  id: string; code: string; shortcut: string; title: string; titleHi: string | null; version: number; contexts: string[];
}
interface Favourite { id: string; context: TemplateContext; phrase: string; shortcut: string | null; label: string | null; useCount: number }
interface ContextView { templates: Template[]; favourites: Favourite[]; ai: { configured: boolean } }
interface Organised { draft: string; unsure: string[]; flags: string[]; model: string; at: string }

type Entry = ShortcutEntry & { template?: Template; favourite?: Favourite };

/** Whether this login may compose in any of these contexts. HR never can. */
export function canCompose(permissions: ReadonlySet<string>, contexts: TemplateContext[]): boolean {
  return contexts.some((c) => CONTEXT_PERMISSIONS[c].some((p) => permissions.has(p)));
}

// ── Speech (browser Web Speech API; no audio leaves the browser for us) ──
interface SpeechResultEvent { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }
interface SpeechRec {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((e: SpeechResultEvent) => void) | null; onend: (() => void) | null; onerror: ((e: { error: string }) => void) | null;
  start(): void; stop(): void;
}
function speechCtor(): (new () => SpeechRec) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** The "/tok" being typed at the caret, if any. */
function slashToken(text: string, caret: number): { start: number; token: string } | null {
  const before = text.slice(0, caret);
  const m = /(^|\s)(\/[a-z0-9-]*)$/i.exec(before);
  if (!m) return null;
  return { start: caret - m[2]!.length, token: m[2]! };
}

function join(a: string, b: string): string {
  const x = a.trimEnd();
  const y = b.trim();
  if (!x) return y;
  if (!y) return x;
  return `${x}${/[.!?]$/.test(x) ? ' ' : '. '}${y}`;
}

export function ClinicalComposerButton({ contexts, value, onAccept, label = 'Compose', disabled, className }: {
  contexts: TemplateContext[];
  value: string;
  onAccept: (text: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { permissions } = useAuth();
  const [open, setOpen] = useState(false);
  const allowed = contexts.filter((c) => CONTEXT_PERMISSIONS[c].some((p) => permissions.has(p)));
  if (!allowed.length) return null;
  return (
    <>
      <Button
        type="button" size="sm" variant="ghost" disabled={disabled} className={className}
        icon={<Wand2 aria-hidden className="h-3.5 w-3.5" />}
        onClick={() => setOpen(true)}
        title="Shortcuts, templates, favourites, voice and AI drafting"
      >
        {label}
      </Button>
      {open && (
        <ClinicalComposer
          contexts={allowed} current={value}
          onClose={() => setOpen(false)}
          onAccept={(t) => { onAccept(t); setOpen(false); }}
        />
      )}
    </>
  );
}

export function ClinicalComposer({ contexts, current, onAccept, onClose }: {
  contexts: TemplateContext[];
  /** What the field already says; "Add to note" appends to it. */
  current: string;
  onAccept: (text: string) => void;
  onClose: () => void;
}) {
  const primary = contexts[0]!;
  const [view, setView] = useState<ContextView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    Promise.all(contexts.map((c) => api.get<ContextView>(`/clinical-templates?context=${c}`)))
      .then((views) => {
        if (!live) return;
        const seen = new Set<string>();
        const templates = views.flatMap((v) => v.templates).filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
        const favSeen = new Set<string>();
        const favourites = views.flatMap((v) => v.favourites).filter((f) => (favSeen.has(f.id) ? false : (favSeen.add(f.id), true)));
        setView({ templates, favourites, ai: views[0]?.ai ?? { configured: false } });
      })
      .catch((e) => { if (live) setLoadError(errorMessage(e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexts.join(','), reload]);

  const [draft, setDraft] = useState('');
  const [usedAi, setUsedAi] = useState(false);
  const [checked, setChecked] = useState(false);
  const [open, setOpenTemplate] = useState<Template | null>(null);
  const [caret, setCaret] = useState(0);
  const [pick, setPick] = useState(0);
  const focusDraft = () => requestAnimationFrame(() => document.getElementById('composer-draft')?.focus());

  const entries: Entry[] = useMemo(() => [
    ...(view?.templates ?? []).map((t) => ({ id: t.id, shortcut: t.shortcut, title: t.title, kind: 'template' as const, template: t })),
    ...(view?.favourites ?? []).filter((f) => f.shortcut).map((f) => ({
      id: f.id, shortcut: f.shortcut!, title: f.label || f.phrase.slice(0, 60), kind: 'favourite' as const, favourite: f,
    })),
  ], [view]);

  const tok = slashToken(draft, caret);
  const resolution = tok ? resolveShortcut(tok.token, entries) : { kind: 'none' as const };
  const suggestions: Entry[] = !tok ? [] : tok.token === '/' ? entries
    : resolution.kind === 'choices' ? resolution.entries : resolution.kind === 'match' ? [resolution.entry] : [];

  function choose(e: Entry) {
    if (!tok) return;
    const before = draft.slice(0, tok.start);
    const after = draft.slice(tok.start + tok.token.length);
    if (e.favourite) {
      setDraft(`${before}${e.favourite.phrase}${after}`);
      void api.post(`/clinical-favourites/${e.favourite.id}/used`, {}).catch(() => undefined);
    } else {
      setDraft(`${before}${after}`.replace(/\s{2,}/g, ' '));
      setOpenTemplate(e.template ?? null);
    }
    setPick(0);
    focusDraft();
  }

  function onKey(ev: KeyboardEvent<HTMLTextAreaElement>) {
    if (!suggestions.length) return;
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setPick((p) => (p + 1) % suggestions.length); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setPick((p) => (p - 1 + suggestions.length) % suggestions.length); }
    else if (ev.key === 'Enter' || ev.key === 'Tab') { ev.preventDefault(); choose(suggestions[Math.min(pick, suggestions.length - 1)]!); }
    else if (ev.key === 'Escape') { ev.stopPropagation(); setCaret(-1); }
  }

  // ── Voice ──
  const Speech = speechCtor();
  const [lang, setLang] = useState<'hi-IN' | 'en-IN'>('hi-IN');
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [spoke, setSpoke] = useState(false);
  const rec = useRef<SpeechRec | null>(null);
  useEffect(() => () => rec.current?.stop(), []);
  function toggleMic() {
    if (!Speech) return;
    if (listening) { rec.current?.stop(); return; }
    const r = new Speech();
    r.lang = lang; r.continuous = true; r.interimResults = true;
    r.onresult = (e) => {
      let fin = ''; let mid = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        if (res.isFinal) fin += res[0].transcript; else mid += res[0].transcript;
      }
      if (fin) { setDraft((d) => (d.trim() ? `${d.trimEnd()} ${fin.trim()}` : fin.trim())); setSpoke(true); }
      setInterim(mid);
    };
    r.onend = () => { setListening(false); setInterim(''); };
    r.onerror = (e) => { setListening(false); setAiError(e.error === 'not-allowed' ? 'The microphone was not allowed.' : `Voice input stopped (${e.error}).`); };
    rec.current = r;
    setAiError(null);
    r.start();
    setListening(true);
  }

  // ── AI ──
  const [busy, setBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [organised, setOrganised] = useState<Organised | null>(null);
  async function organise() {
    setBusy(true); setAiError(null); setOrganised(null);
    try {
      setOrganised(await api.post<Organised>('/clinical-templates/organise', {
        context: primary, text: draft, ...(spoke ? { spokenLanguage: lang } : {}),
      }));
    } catch (e) { setAiError(errorMessage(e)); } finally { setBusy(false); }
  }

  // ── Favourites ──
  const [savingFav, setSavingFav] = useState(false);
  const [favShortcut, setFavShortcut] = useState('');
  const [favError, setFavError] = useState<string | null>(null);
  async function saveFavourite() {
    setFavError(null);
    try {
      await api.post('/clinical-favourites', {
        context: primary, phrase: draft.trim(), ...(favShortcut.trim() ? { shortcut: favShortcut.trim() } : {}),
      });
      setSavingFav(false); setFavShortcut(''); setReload((n) => n + 1);
    } catch (e) { setFavError(errorMessage(e)); }
  }
  async function removeFavourite(f: Favourite) {
    try { await api.del(`/clinical-favourites/${f.id}`); setReload((n) => n + 1); } catch (e) { setFavError(errorMessage(e)); }
  }

  const ready = draft.trim().length > 0 && (!usedAi || checked);
  const title = contexts.length === 1 ? TEMPLATE_CONTEXT_LABEL[primary] : 'Compose clinical text';

  return (
    <Dialog
      title={title} wide onClose={onClose}
      info="Type / for a shortcut, press a template, or speak. Templates write fixed wording; AI only tidies what you wrote and is marked as a draft. Nothing reaches the record until you add it."
      footer={(
        <div className="flex w-full flex-wrap items-center justify-end gap-2 max-md:flex-col-reverse max-md:items-stretch">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          {current.trim() && (
            <Button variant="secondary" disabled={!ready} onClick={() => onAccept(draft.trim())}>Replace note</Button>
          )}
          <Button disabled={!ready} onClick={() => onAccept(join(current, draft))}>
            {current.trim() ? 'Add to note' : 'Use this text'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        {loadError && <Alert tone="danger">{loadError}</Alert>}

        {/* Templates */}
        {view && view.templates.length > 0 && (
          <section aria-label="Templates">
            <h3 className="mb-1.5 text-caption text-neutral-600">Templates</h3>
            <div className="flex flex-wrap gap-1.5">
              {view.templates.map((t) => (
                <button
                  key={t.id} type="button" onClick={() => setOpenTemplate(open?.id === t.id ? null : t)}
                  aria-pressed={open?.id === t.id}
                  className={cn('h-8 rounded-btn border px-2.5 text-small max-md:h-10',
                    open?.id === t.id ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700'
                      : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50')}
                >
                  {t.title}{t.titleHi ? <span className="ml-1 text-neutral-500">· {t.titleHi}</span> : null}
                  <span className="ml-1.5 font-mono text-xs text-neutral-500">{t.shortcut}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {open && (
          <TemplatePanel
            key={open.id} template={open}
            onInsert={(text) => { setDraft((d) => join(d, text)); setOpenTemplate(null); focusDraft(); }}
            onClose={() => setOpenTemplate(null)}
          />
        )}

        {/* Draft */}
        <section aria-label="Draft">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <label htmlFor="composer-draft" className="text-caption text-neutral-600">Draft</label>
            {Speech && (
              <div className="flex items-center gap-1.5">
                <Select aria-label="Spoken language" value={lang} disabled={listening}
                  onChange={(e) => setLang(e.target.value as 'hi-IN' | 'en-IN')} className="h-8 w-32 text-small max-md:h-10">
                  <option value="hi-IN">Hindi / Hinglish</option>
                  <option value="en-IN">English</option>
                </Select>
                <Button size="sm" variant={listening ? 'danger' : 'secondary'} onClick={toggleMic}
                  icon={listening ? <MicOff aria-hidden className="h-3.5 w-3.5" /> : <Mic aria-hidden className="h-3.5 w-3.5" />}>
                  {listening ? 'Stop' : 'Speak'}
                </Button>
              </div>
            )}
          </div>
          <div className="relative">
            <Textarea
              id="composer-draft" rows={5} value={draft}
              placeholder="Type, speak, or start with / for a shortcut (/history, /exam, /advice …)"
              onChange={(e) => { setDraft(e.target.value); setCaret(e.target.selectionStart ?? e.target.value.length); setPick(0); }}
              onKeyUp={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              onClick={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              onKeyDown={onKey}
              aria-autocomplete="list"
              aria-expanded={suggestions.length > 0}
            />
            {suggestions.length > 0 && (
              <ul role="listbox" aria-label="Shortcuts"
                className="absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-auto rounded-input border border-neutral-200 bg-white py-1 shadow-lg">
                {resolution.kind === 'choices' && tok!.token !== '/' && (
                  <li className="px-3 py-1 text-caption text-neutral-500">More than one matches — choose:</li>
                )}
                {suggestions.map((e, i) => (
                  <li key={`${e.kind}-${e.id}`} role="option" aria-selected={i === pick}>
                    <button type="button" onMouseDown={(ev) => { ev.preventDefault(); choose(e); }}
                      className={cn('flex w-full items-center gap-2 px-3 py-1.5 text-left text-small max-md:min-h-10',
                        i === pick ? 'bg-primary-50 text-primary-800' : 'text-neutral-800 hover:bg-neutral-50')}>
                      <span className="font-mono text-xs text-neutral-500">{e.shortcut}</span>
                      <span className="min-w-0 flex-1 truncate">{e.title}</span>
                      {e.kind === 'favourite' && <Star aria-label="favourite" className="h-3.5 w-3.5 text-dueSoon-600" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {interim && <p className="mt-1 text-caption italic text-neutral-500">Hearing: {interim}</p>}
          {!Speech && <p className="mt-1 text-caption text-neutral-500">Voice input is not available in this browser.</p>}
        </section>

        {/* AI */}
        <section aria-label="Organise with AI" className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" loading={busy}
              disabled={busy || draft.trim().length < 2 || view?.ai.configured === false}
              onClick={organise} icon={<Sparkles aria-hidden className="h-3.5 w-3.5" />}
              title="Turns what you typed or spoke into a clean English draft. It adds nothing of its own.">
              Organise with AI
            </Button>
            {view?.ai.configured === false && (
              <span className="text-caption text-neutral-500">AI is not set up on this server — type the note yourself.</span>
            )}
          </div>
          {aiError && <Alert tone="warning">{aiError}</Alert>}
          {organised && (
            <div className="rounded-input border border-dashed border-primary-300 bg-primary-50/40 p-3">
              <p className="text-caption font-semibold uppercase tracking-wide text-primary-700">
                AI draft — check before use <span className="font-normal normal-case text-neutral-500">· {organised.model}</span>
              </p>
              <p className="mt-1 whitespace-pre-line text-small text-neutral-900">{organised.draft}</p>
              {organised.flags.length > 0 && (
                <Alert tone="warning" className="mt-2">
                  The draft uses words your notes did not: {organised.flags.join(', ')}. Remove them unless you meant them.
                </Alert>
              )}
              {organised.unsure.length > 0 && (
                <div className="mt-2 text-caption text-neutral-700">
                  <span className="font-medium">It was unsure about:</span>
                  <ul className="ml-4 list-disc">{organised.unsure.map((u) => <li key={u}>{u}</li>)}</ul>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => { setDraft(organised.draft); setUsedAi(true); setChecked(false); setOrganised(null); }}>
                  Use AI draft
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOrganised(null)}>Discard</Button>
              </div>
            </div>
          )}
          {usedAi && (
            <Checkbox
              label="I have read this AI-assisted draft and it says only what I found."
              checked={checked} onChange={(e) => setChecked(e.target.checked)}
            />
          )}
        </section>

        {/* Favourites */}
        <section aria-label="My favourites" className="border-t border-neutral-200 pt-3">
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-caption text-neutral-600">My favourites</h3>
            {!savingFav ? (
              <Button size="sm" variant="ghost" disabled={!draft.trim()} onClick={() => setSavingFav(true)}
                icon={<Star aria-hidden className="h-3.5 w-3.5" />}>Save draft as favourite</Button>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5 max-md:w-full">
                <Input aria-label="Shortcut (optional)" placeholder="/shortcut (optional)" value={favShortcut}
                  onChange={(e) => setFavShortcut(e.target.value)} className="h-8 w-44 text-small max-md:h-10 max-md:flex-1" />
                <Button size="sm" onClick={saveFavourite}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => { setSavingFav(false); setFavError(null); }}>Cancel</Button>
              </div>
            )}
          </div>
          {favError && <Alert tone="danger" className="mb-2">{favError}</Alert>}
          {view && view.favourites.length === 0 && (
            <p className="text-caption text-neutral-500">Phrases you use often, saved here, are one tap away next time.</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {view?.favourites.map((f) => (
              <span key={f.id} className="inline-flex max-w-full items-center rounded-btn border border-neutral-300 bg-white text-small">
                <button type="button" className="max-w-[18rem] truncate px-2.5 py-1 text-left hover:bg-neutral-50 max-md:min-h-10"
                  title={f.phrase}
                  onClick={() => { setDraft((d) => join(d, f.phrase)); void api.post(`/clinical-favourites/${f.id}/used`, {}).catch(() => undefined); }}>
                  {f.shortcut && <span className="mr-1 font-mono text-xs text-neutral-500">{f.shortcut}</span>}
                  {f.label || f.phrase}
                </button>
                <button type="button" aria-label={`Remove favourite ${f.label || f.phrase.slice(0, 30)}`}
                  className="border-l border-neutral-200 px-1.5 py-1 text-neutral-500 hover:text-danger-600 max-md:min-h-10"
                  onClick={() => removeFavourite(f)}>
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        </section>
      </div>
    </Dialog>
  );
}

/** One template: quick-select chips, the blanks it needs, and a live preview. */
function TemplatePanel({ template, onInsert, onClose }: {
  template: Template; onInsert: (text: string) => void; onClose: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const r = renderTemplate(template, { selected, values });
  const toggle = (code: string) => setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
  const label = (f: TemplateField) => f.label + (f.labelHi ? ` · ${f.labelHi}` : '');

  return (
    <section aria-label={template.title} className="rounded-input border border-neutral-200 bg-neutral-50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-small font-semibold text-neutral-900">
          {template.title} <span className="font-mono text-xs font-normal text-neutral-500">{template.shortcut}</span>
        </h3>
        <button type="button" aria-label="Close template" onClick={onClose} className="text-neutral-500 hover:text-neutral-900 max-md:min-h-10">
          <X aria-hidden className="h-4 w-4" />
        </button>
      </div>

      {template.options.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Quick select">
          {template.options.map((o) => {
            const on = selected.includes(o.code);
            return (
              <button key={o.code} type="button" aria-pressed={on} onClick={() => toggle(o.code)} title={o.sentence}
                className={cn('h-8 rounded-full border px-3 text-small max-md:h-10',
                  on ? 'border-primary-600 bg-primary-600 text-white' : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100')}>
                {o.label}{o.labelHi ? <span className={cn('ml-1', on ? 'text-white/80' : 'text-neutral-500')}>· {o.labelHi}</span> : null}
              </button>
            );
          })}
        </div>
      )}

      {template.fields.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {template.fields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-caption text-neutral-700">{label(f)}</span>
              <div className="mt-0.5">
                <FieldInput field={f} value={values[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
              </div>
            </label>
          ))}
        </div>
      )}

      {r.conflicts.length > 0 && (
        <Alert tone="warning" className="mt-2">
          Choose one: {r.conflicts.map((c) => c.codes.map((code) => template.options.find((o) => o.code === code)?.label ?? code).join(' or ')).join('; ')}.
        </Alert>
      )}

      <div className="mt-3 rounded-input border border-neutral-200 bg-white p-2">
        <p className="text-caption text-neutral-500">Will write</p>
        <p className={cn('text-small', r.text ? 'text-neutral-900' : 'text-neutral-500')}>
          {r.text || 'Nothing yet — press an option or fill a blank. Blanks left empty are left out.'}
        </p>
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" disabled={!r.text || r.conflicts.length > 0} onClick={() => onInsert(r.text)}>Insert into draft</Button>
      </div>
    </section>
  );
}

function FieldInput({ field, value, onChange }: { field: TemplateField; value: FieldValue; onChange: (v: FieldValue) => void }) {
  const cls = 'h-9 text-small max-md:h-10';
  if (field.type === 'choice') {
    return (
      <Select value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)} className={cls}>
        <option value="">—</option>
        {(field.choices ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}{c.labelHi ? ` · ${c.labelHi}` : ''}</option>)}
      </Select>
    );
  }
  if (field.type === 'duration') {
    const d = (value && typeof value === 'object' ? value : { amount: '', unit: 'day' }) as { amount: string | number; unit: string };
    return (
      <div className="flex gap-1.5">
        <Input inputMode="numeric" aria-label={`${field.label} amount`} value={String(d.amount ?? '')} className={cn(cls, 'w-20')}
          onChange={(e) => onChange({ amount: e.target.value, unit: d.unit })} />
        <Select aria-label={`${field.label} unit`} value={d.unit} className={cls}
          onChange={(e) => onChange({ amount: d.amount, unit: e.target.value })}>
          {DURATION_UNITS.map((u) => <option key={u} value={u}>{u}s</option>)}
        </Select>
      </div>
    );
  }
  if (field.type === 'date') {
    return <Input type="date" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} className={cls} />;
  }
  return (
    <div className="flex items-center gap-1.5">
      <Input
        value={value == null || typeof value === 'object' ? '' : String(value)}
        inputMode={field.type === 'number' || field.type === 'unit' ? 'decimal' : 'text'}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)} className={cls}
      />
      {field.type === 'unit' && field.unit && <span className="text-caption text-neutral-500">{field.unit}</span>}
    </div>
  );
}
